import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { object, bool } from "prop-types";

import FollowUs from "../follow-us";

import Authors from "../../../atoms/author";
import SocialMediaShare from "../../../molecules/social-media-share";

import "./author-and-timestamp.m.css";
import CommentsCount from "../../../atoms/comments-count";
import Share from "../../../atoms/other-icons/Share";
import useAdType from "../../../helper/useAdType";
import { AdContainer } from "../../../ads";
import PreferredNewsButton from "../preferred-news";

const AuthorAndTimeStamp = ({
  story,
  isDHExclusive,
  isSponsoredStory,
  isBrandspotNoAdStory,
  isBrandspotClientStory
}) => {
  const [showSharingOptions, setShowSharingOptions] = useState(false);

  const lastUpdatedDateAndTime = format(
    new Date(story && story["last-published-at"]),
    "dd MMMM yyyy, HH:mm"
  );

  const shareRef = useRef(null);
  const [timestampShareWidth, setTimestampShareWidth] = useState("auto");
  const [stickyLeft, setStickyLeft] = useState(undefined);
  const [isMobile, setMobile] = useState(true);

  const adType = useAdType();

  const tempHideAd =
    story?.["story-template"] === "brandspot-with-no-ad" ||
    story?.slug?.includes(
      "dhbrandspot-pr/best-crypto-casinos-2025-my-go-to-sites-for-bitcoin-gambling-3470705"
    );

  useEffect(() => {
    if (window && window.innerWidth < 1024) {
      setMobile(true);
    } else {
      setMobile(false);
    }
  }, []);

  const componentRef = useRef(null);
  const stickyDivRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isStickyAlt, setIsStickyAlt] = useState(false);
  const [endOfPage, setEndOfPage] = useState(false);

  const [stickyTop, setStickyTop] = useState(!isMobile ? "108px" : "0px");

  useEffect(() => {
    const desktopHeader = document.querySelector("#desk-header");
    const primaryMenu = document.querySelector(".primary-menu");
    let previousScrollY = window.scrollY;

    const handleScroll = () => {
      if (typeof window === "undefined") return;
      window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const scrollDifference = currentScrollY - previousScrollY;
        const scrollThreshold = 20;

        if (Math.abs(scrollDifference) < scrollThreshold) {
          return;
        }

        const isScrollingUp = currentScrollY < previousScrollY;
        const totalPageEnd = window.innerHeight + currentScrollY >= document.documentElement.scrollHeight - 75;

        const secondaryHeader =
          document.querySelector("#desk-secondary-header") ||
          document.querySelector(".secondary-menu");
        const isSecondaryHeaderVisible =
          secondaryHeader &&
          parseFloat(window.getComputedStyle(secondaryHeader).opacity) > 0;

        const stickyShare = stickyDivRef.current;
        const stickyShareBottom = stickyShare?.getBoundingClientRect().bottom;
        const stickyShareHeight = stickyShare?.getBoundingClientRect().height || 0;

        const storyContainer =
          componentRef.current?.closest(".story-section") || componentRef.current?.closest(".story-wrapper");
        const storyCardsElem = storyContainer?.querySelector(".story-cards") || storyContainer;
        const containerBottom = storyCardsElem?.getBoundingClientRect().bottom;

        const taboolaAdWrapper =
          document.querySelector("#ad-wrapper-taboola") || document.querySelector("[id^='taboola-below-article']");

        const header = document.querySelector("#desk-header") || document.querySelector("#mob-header");
        let headerBottom = 0;
        if (header) {
          if (isMobile && header.id === "mob-header") {
            const blackBar = Array.from(header.children).find(c => c.classList.contains("primary-menu"));
            if (blackBar) {
              const blackBarOpacity = parseFloat(window.getComputedStyle(blackBar).opacity);
              headerBottom = blackBarOpacity > 0.95
                ? Math.max(0, blackBar.getBoundingClientRect().bottom)
                : 0;
            }
          } else {
            headerBottom = header.getBoundingClientRect().bottom || 0;
            if (secondaryHeader && isSecondaryHeaderVisible) {
              headerBottom = headerBottom + secondaryHeader.offsetHeight;
            }
          }
        }
        const triggerPoint = headerBottom;

        const isAtContainerEnd =
          containerBottom !== undefined && containerBottom <= triggerPoint + stickyShareHeight + 10;
        const reachedTaboola =
          taboolaAdWrapper?.getBoundingClientRect().top !== undefined &&
          taboolaAdWrapper.getBoundingClientRect().top <= triggerPoint + stickyShareHeight + 10;

        setEndOfPage(totalPageEnd);

        if (stickyShareBottom < -20) {
          setIsStickyAlt(true);
        } else {
          setIsStickyAlt(false);
        }

        if (totalPageEnd || isAtContainerEnd || reachedTaboola) {
          setIsSticky(false);
          previousScrollY = currentScrollY;
          return;
        }

        if (!isScrollingUp || (!isMobile && !isSecondaryHeaderVisible)) {
          setIsSticky(false);
          previousScrollY = currentScrollY;
          return;
        }

        const authorWrapper = componentRef.current?.querySelector(".author-wrapper-id");
        const authorWrapperBottom = (authorWrapper || componentRef.current)?.getBoundingClientRect().bottom;

        const authorTop = componentRef.current?.getBoundingClientRect().top || 0;

        if (isMobile) {
          const wrapperBottom = componentRef.current?.getBoundingClientRect().bottom || 0;
          const naturalFollowBarTop = wrapperBottom - stickyShareHeight;
          if (naturalFollowBarTop <= triggerPoint) {
            setIsSticky(true);
            const effectiveStickyTop = wrapperBottom < -100 ? 0 : triggerPoint;
            setStickyTop(`${effectiveStickyTop}px`);
          } else {
            setIsSticky(false);
          }
        } else {
          const authorId = document.querySelector("#author-resizer-wrapper");
          const desktopAuthorTop = authorId?.getBoundingClientRect().top || 0;

          if (isSponsoredStory || isBrandspotNoAdStory || isBrandspotClientStory) {
            triggerPoint >= desktopAuthorTop + 10 ? setIsSticky(true) : setIsSticky(false);
          } else {
            triggerPoint >= authorWrapperBottom + 10 ? setIsSticky(true) : setIsSticky(false);
          }
          setStickyTop(`${triggerPoint}px`);
        }
        previousScrollY = currentScrollY;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSharingOptions &&
        shareRef.current &&
        !shareRef.current.contains(event.target)
      ) {
        setShowSharingOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [showSharingOptions]);

  useEffect(() => {
    const updateStickyDimensions = () => {
      if (!stickyDivRef.current) return;

      if (isMobile) {
        setTimestampShareWidth(`${window.innerWidth - 32}px`);
        setStickyLeft(undefined);
        return;
      }

      const storyContainer =
        componentRef.current?.closest(".story-section") || componentRef.current?.closest(".story-wrapper");
      const targetWidth = componentRef.current?.offsetWidth || stickyDivRef.current.offsetWidth;
      const targetLeft = componentRef.current?.getBoundingClientRect().left || storyContainer?.getBoundingClientRect().left;

      setTimestampShareWidth(`${targetWidth}px`);
      setStickyLeft(targetLeft !== undefined ? `${targetLeft}px` : undefined);
    };

    updateStickyDimensions();
    window.addEventListener("resize", updateStickyDimensions);

    return () => {
      window.removeEventListener("resize", updateStickyDimensions);
    };
  }, [isMobile, isSticky]);

  const sharingHandler = async () => {
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: story?.headline,
          text: story?.headline,
          url: story?.url
        });
      } catch (err) { }
      return;
    }

    setShowSharingOptions((prev) => !prev);
  };

  const DhExlusiveDesktop = isDHExclusive && !isMobile;

  return (
    <div styleName="wrapper" ref={componentRef}>
      {!isSponsoredStory && !isBrandspotNoAdStory && !isBrandspotClientStory && !isDHExclusive && (
        <div className="author-wrapper-id">
          <Authors
            authors={story.authors}
            clazzName="author-name-image"
            lastUpdatedDateAndTime={lastUpdatedDateAndTime}
          />
        </div>
      )}
      {isMobile && !tempHideAd ? (
        <div
          styleName="ad-wrapper mobile-top-ad"
          className="hide-desktop"
        >
          <AdContainer
            storyPageAds={true}
            AdClassName="standard-336x280"
            id={
              adType === "izooto"
                ? "div-gpt-ad-1711971869798-0"
                : "div-gpt-ad-1750399308852-0"
            }
            mobile={
              adType === "izooto"
                ? "DH_MWeb_AT_Top_ym"
                : "DH_MWeb_AT_Top"
            }
            isExternalAdPath={adType === "izooto"}
          />
        </div>
      ) : null}

      {!DhExlusiveDesktop && <div styleName={` ${isSticky ? "alternate-wrapper" : ""}`}></div>}
      <div
        styleName={`timestamp-share-wrapper ${isDHExclusive ? "less-space" : ""} ${!DhExlusiveDesktop ? ` ${isSticky ? "show" : isStickyAlt ? "hide" : ""}` : ""}`}
        className={`sticky-div ${isSticky && !DhExlusiveDesktop ? "sticky" : ""}`}

        ref={stickyDivRef}
        style={{
          width: isMobile && isSticky ? "calc(100vw - 32px)" : timestampShareWidth,
          zIndex: isSticky ? "100" : "3",
          transition: "transform 0.3s ease, opacity 0.3s ease, top 0.3s ease",
          ...(isSticky ? {
            top: stickyTop,
            marginTop: "0",
            left: isMobile ? "16px" : stickyLeft
          } : {})
        }}
      >

        <div styleName="author-resize-wrapper-contents">
          <FollowUs />

          <div styleName="comment-share-wrapper">
            <PreferredNewsButton />

            {story["story-template"] !== "live-blog" && (
              <CommentsCount
                story={story}
                showCommentText={false}
                renderPortal={true}
              />
            )}

            <div styleName="share" ref={shareRef}>
              <button
                styleName="share-btn"
                onClick={sharingHandler}
              >
                <Share color="black" height="20px" width="20px" />
              </button>

              <div styleName="share-options">
                {showSharingOptions && (
                  <SocialMediaShare story={story} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AuthorAndTimeStamp.propTypes = {
  story: object,
  isDHExclusive: bool,
  isSponsoredStory: bool,
  isBrandspotClientStory: bool,
  isBrandspotNoAdStory: bool
};

AuthorAndTimeStamp.defaultProps = {
  isDHExclusive: false,
  isSponsoredStory: false,
  isBrandspotClientStory: false,
  isBrandspotNoAdStory: false
};

export default AuthorAndTimeStamp;
